import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Truck, Warning, CheckCircle, MapPin, ShieldCheck, ListMagnifyingGlass, Handshake, Wallet } from "@phosphor-icons/react";
import { LanguageToggle } from "@/components/LanguageToggle";
import { IndiaMandiMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const inr = (n) => (n === null || n === undefined ? "–" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

const VERDICT = {
  worth_it: { text: "Worth it", cls: "bg-emerald-100 text-emerald-900 border-emerald-300", note: "Buying from the seller's state is clearly cheaper than buying locally." },
  tight: { text: "Tight", cls: "bg-amber-100 text-amber-900 border-amber-300", note: "A small saving. A rate change or a higher freight quote can turn it into a loss." },
  break_even: { text: "About equal", cls: "bg-stone-100 text-stone-800 border-stone-300", note: "Freight and costs cancel the price difference. Choose on quality, payment terms and availability." },
  not_worth_it: { text: "Not worth it", cls: "bg-red-100 text-red-900 border-red-300", note: "After freight and costs, buying locally is cheaper." },
};

const STEPS = [
  { icon: ListMagnifyingGlass, title: "Check the rates", text: "See the state-wise rate and whether the other state is really cheaper after freight." },
  { icon: Handshake, title: "Post what you need", text: "Post a buy requirement: commodity, quantity, grade, delivery place. Or take an open sell lot below." },
  { icon: ShieldCheck, title: "Match a verified seller", text: "India Mandi shows the closest, best-priced sellers. Check their KYC and rating before you agree." },
  { icon: Wallet, title: "Pay safely", text: "The deal moves to Payment secured. Money is released after delivery is confirmed." },
  { icon: Truck, title: "Transport, deliver, invoice", text: "A verified transporter moves the load with e-way bill. After delivery a GST invoice is generated." },
];

function Section({ eyebrow, title, children, right }) {
  return (
    <section className="rounded-xl bg-white border border-stone-200 p-5 sm:p-6">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-stone-500">{eyebrow}</div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-stone-900 mt-1">{title}</h2>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Row({ label, value, strong, minus }) {
  return (
    <div className={`flex items-center justify-between py-1.5 text-sm ${strong ? "font-bold text-stone-900 border-t border-stone-200 mt-1 pt-2" : "text-stone-700"}`}>
      <span>{label}</span>
      <span className="font-mono">{minus ? "− " : ""}{value}</span>
    </div>
  );
}

export default function TradeRoute() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  const [commodities, setCommodities] = useState([]);
  const [asOf, setAsOf] = useState(null);
  const [commodity, setCommodity] = useState(params.get("commodity") || "");
  const [rates, setRates] = useState(null);
  const [ratesError, setRatesError] = useState("");
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [qty, setQty] = useState(Number(params.get("qty")) || 100);
  const [fromRate, setFromRate] = useState("");
  const [toRate, setToRate] = useState("");
  const [feePct, setFeePct] = useState("");
  const [freight, setFreight] = useState("");
  const [route, setRoute] = useState(null);
  const [routeError, setRouteError] = useState("");
  const [legal, setLegal] = useState(null);

  // commodities that have state-wise rates today, and the checklist
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rates/state-commodities");
        setAsOf(data.as_of);
        setCommodities(data.items || []);
        if (!commodity && data.items?.length) {
          const pick = data.items.find((i) => i.commodity === "Maize") || data.items[0];
          setCommodity(pick.commodity);
        }
      } catch {}
      try {
        const { data } = await api.get("/trade/legal");
        setLegal(data);
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // state-wise rates of the chosen commodity
  useEffect(() => {
    if (!commodity) return;
    let live = true;
    setRates(null);
    setRatesError("");
    (async () => {
      try {
        const { data } = await api.get("/rates/states", { params: { commodity } });
        if (!live) return;
        setRates(data);
        const names = data.states.map((s) => s.state);
        setFrom((f) => (names.includes(f) ? f : names[0]));
        setTo((t) => (names.includes(t) ? t : names[names.length - 1]));
      } catch (e) {
        if (live) setRatesError(e?.response?.data?.detail || "Could not load rates");
      }
    })();
    return () => { live = false; };
  }, [commodity]);

  useEffect(() => { setFromRate(""); }, [from]);
  useEffect(() => { setToRate(""); }, [to]);

  // the route, recalculated when anything it depends on changes
  useEffect(() => {
    if (!commodity || !from || !to || !(qty > 0) || !rates) return;
    const have = new Set(rates.states.map((s) => s.state));
    const needFrom = !have.has(from), needTo = !have.has(to);
    if ((needFrom && !(Number(fromRate) > 0)) || (needTo && !(Number(toRate) > 0))) {
      setRoute(null);       // wait until the missing rate is typed in
      setRouteError("");
      return;
    }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const p = { commodity, from_state: from, to_state: to, quantity: qty };
        if (needFrom) p.from_rate = Number(fromRate);
        if (needTo) p.to_rate = Number(toRate);
        if (feePct !== "") p.fee_pct = feePct;
        if (freight !== "") p.freight_per_qtl_km = freight;
        const { data } = await api.get("/trade/route", { params: p });
        if (live) { setRoute(data); setRouteError(""); }
      } catch (e) {
        if (live) { setRoute(null); setRouteError(e?.response?.data?.detail || "Could not work out this route"); }
      }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [commodity, from, to, qty, feePct, freight, rates, fromRate, toRate]);

  // keep the address shareable
  useEffect(() => {
    if (!commodity) return;
    setParams({ commodity, ...(from && { from }), ...(to && { to }), qty: String(qty) }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commodity, from, to, qty]);

  const withData = useMemo(() => new Set((rates?.states || []).map((s) => s.state)), [rates]);
  const allStates = useMemo(() => [...(rates?.states || []).map((s) => s.state), ...(rates?.no_data_states || [])], [rates]);
  const lowRate = useMemo(() => Math.min(...(rates?.states || []).map((s) => s.avg_rate), Infinity), [rates]);
  const maxRate = useMemo(() => Math.max(...(rates?.states || []).map((s) => s.avg_rate), 1), [rates]);
  // bars start at 15% for the cheapest state and reach 100% for the dearest, so the gap between states can be seen
  const barWidth = (rate) => (maxRate === lowRate ? 100 : 15 + (85 * (rate - lowRate)) / (maxRate - lowRate));
  const v = route ? VERDICT[route.verdict] : null;
  // a logged-in user lands on the listing form with the route already filled in
  const post = () => {
    if (!user) return nav("/register");
    const q = new URLSearchParams({ create: "1", type: "buy", commodity: route?.commodity || commodity, state: to, qty: String(qty) });
    if (route) {
      q.set("price", String(Math.round(route.per_quintal.source_rate)));
      q.set("notes", `Looking for ${route.commodity} from ${route.from_state.state}, delivered to ${route.to_state.state}. Estimated landed cost Rs ${Math.round(route.per_quintal.landed_cost)}/qtl.`);
    }
    nav(`/app/listings?${q.toString()}`);
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="trade-page">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-8 py-3">
          <Link to="/" className="flex items-center gap-2"><IndiaMandiMark size={36} /></Link>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            {user ? (
              <Link to="/app" className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-emerald-900 text-white font-semibold text-sm">Dashboard <ArrowRight size={16} /></Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline-block px-3 py-2 text-sm font-semibold text-stone-700">Log In</Link>
                <Link to="/register" className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-semibold text-sm">Get Started <ArrowRight size={16} /></Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8 pb-28 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-orange-600">State rates · Trade route</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-emerald-950 mt-1">Buy from the right state.</h1>
          <p className="text-stone-600 mt-2 max-w-2xl">
            See today's rate of a commodity in every state, then check what it really costs to bring it from one state to another, with freight and fees, before you call anyone.
          </p>
        </div>

        <Section
          eyebrow="Step 1"
          title="State-wise rate"
          right={
            <label className="text-sm font-semibold text-stone-700 flex items-center gap-2">
              Commodity
              <select value={commodity} onChange={(e) => setCommodity(e.target.value)} className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="trade-commodity">
                {commodities.map((c) => (
                  <option key={c.commodity} value={c.commodity}>{c.commodity} ({c.states} states)</option>
                ))}
              </select>
            </label>
          }
        >
          {ratesError && <div className="text-sm text-red-700">{ratesError}</div>}
          {!rates && !ratesError && <div className="h-40 rounded shimmer" />}
          {rates && (
            <>
              <div className="text-sm text-stone-600 mb-3">
                National average <b className="text-stone-900">{inr(rates.national_avg)}</b> per quintal · rates of {rates.as_of}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-stone-500">
                      <th className="py-2 pr-3">State</th>
                      <th className="py-2 pr-3 w-[38%]">Rate per quintal</th>
                      <th className="py-2 pr-3 text-right">vs national</th>
                      <th className="py-2 pr-3 text-right">Mandis</th>
                      <th className="py-2 text-right">Route</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.states.map((s, i) => (
                      <tr key={s.state} className="border-t border-stone-100" data-testid={`state-row-${s.state}`}>
                        <td className="py-2 pr-3 font-semibold">
                          {s.state}
                          {i === 0 && <span className="ml-2 text-[10px] font-bold uppercase text-emerald-700">cheapest</span>}
                          {i === rates.states.length - 1 && <span className="ml-2 text-[10px] font-bold uppercase text-orange-700">dearest</span>}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            <div className="h-2 rounded bg-stone-100 flex-1"><div className="h-2 rounded bg-emerald-700" style={{ width: `${barWidth(s.avg_rate)}%` }} /></div>
                            <span className="font-mono w-24 text-right">{inr(s.avg_rate)}</span>
                          </div>
                        </td>
                        <td className={`py-2 pr-3 text-right font-mono ${s.vs_national_pct <= 0 ? "text-emerald-700" : "text-orange-700"}`}>{s.vs_national_pct > 0 ? "+" : ""}{s.vs_national_pct}%</td>
                        <td className="py-2 pr-3 text-right text-stone-600" title={s.n_mandis < 3 ? "Few mandis reported: treat this rate as indicative" : ""}>{s.n_mandis}{s.n_mandis < 3 ? " *" : ""}</td>
                        <td className="py-2 text-right whitespace-nowrap">
                          <button onClick={() => setFrom(s.state)} className={`px-2 py-1 rounded text-xs font-bold border ${from === s.state ? "bg-emerald-900 text-white border-emerald-900" : "border-stone-300 text-stone-700 hover:bg-stone-100"}`}>Buy from</button>{" "}
                          <button onClick={() => setTo(s.state)} className={`px-2 py-1 rounded text-xs font-bold border ${to === s.state ? "bg-orange-600 text-white border-orange-600" : "border-stone-300 text-stone-700 hover:bg-stone-100"}`}>Deliver to</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-stone-500 mt-3">* fewer than 3 mandis reported, indicative only. Bars start from the cheapest state to show the gap. {rates.source}</p>
            </>
          )}
        </Section>

        <div id="trade-route" className="scroll-mt-24" />
        <Section eyebrow="Step 2" title="Trade route and landed cost">
          {rates && (
            <div className="grid sm:grid-cols-4 gap-3 mb-5">
              <label className="text-sm font-semibold text-stone-700">Buy from (seller's state)
                <select value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="trade-from">
                  {allStates.map((n) => <option key={n} value={n}>{n}{withData.has(n) ? "" : " (no rate today)"}</option>)}
                </select>
              </label>
              <label className="text-sm font-semibold text-stone-700">Deliver to (buyer's state)
                <select value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="trade-to">
                  {allStates.map((n) => <option key={n} value={n}>{n}{withData.has(n) ? "" : " (no rate today)"}</option>)}
                </select>
              </label>
              <label className="text-sm font-semibold text-stone-700">Quantity (quintal)
                <input type="number" min="1" value={qty} onChange={(e) => setQty(Number(e.target.value))} className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="trade-qty" />
              </label>
              <details className="text-sm sm:self-end">
                <summary className="cursor-pointer font-semibold text-stone-700 py-2">Change assumptions</summary>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <label className="text-xs text-stone-600">Fee + commission %<input type="number" step="0.1" min="0" placeholder="2" value={feePct} onChange={(e) => setFeePct(e.target.value)} className="w-full rounded border border-stone-300 px-2 py-1" /></label>
                  <label className="text-xs text-stone-600">Freight ₹/qtl/km<input type="number" step="0.01" min="0" placeholder="0.22" value={freight} onChange={(e) => setFreight(e.target.value)} className="w-full rounded border border-stone-300 px-2 py-1" /></label>
                </div>
              </details>
            </div>
          )}

          {rates && (!withData.has(from) || !withData.has(to)) && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 mb-5 grid sm:grid-cols-2 gap-3" data-testid="trade-manual-rate">
              {!withData.has(from) && (
                <label className="font-semibold">
                  No {commodity} rate reported for {from} today. Enter the rate you were quoted (₹ per quintal)
                  <input type="number" min="1" value={fromRate} onChange={(e) => setFromRate(e.target.value)} className="mt-1 w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm font-normal" data-testid="trade-from-rate" />
                </label>
              )}
              {!withData.has(to) && (
                <label className="font-semibold">
                  No {commodity} rate reported for {to} today. Enter the local rate (₹ per quintal)
                  <input type="number" min="1" value={toRate} onChange={(e) => setToRate(e.target.value)} className="mt-1 w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm font-normal" data-testid="trade-to-rate" />
                </label>
              )}
            </div>
          )}

          {routeError && <div className="text-sm text-red-700">{routeError}</div>}
          {route && v && (
            <div className="grid lg:grid-cols-5 gap-5" data-testid="trade-result">
              <div className="lg:col-span-3 rounded-lg border border-stone-200 p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 font-display font-bold text-lg"><MapPin size={20} className="text-emerald-800" /> {route.from_state.state} <ArrowRight size={16} /> {route.to_state.state}</div>
                  <span className={`px-3 py-1 rounded-full border text-sm font-bold ${v.cls}`}>{v.text}</span>
                </div>
                <div className="text-sm text-stone-600 mt-1">≈ {route.distance_km.toLocaleString("en-IN")} km by road · about {route.transit_days} days on the road</div>
                <div className="mt-3">
                  <Row label={`${route.commodity} at ${route.from_state.state}${route.from_state.manual ? " (your rate)" : ""}`} value={inr(route.per_quintal.source_rate)} />
                  <Row label="Freight (estimate)" value={inr(route.per_quintal.freight)} />
                  <Row label={`Mandi fee + commission (${route.assumptions.fee_pct}%)`} value={inr(route.per_quintal.fee_commission)} />
                  <Row label={`Loss in weight (${route.assumptions.shrink_pct}%)`} value={inr(route.per_quintal.shrinkage)} />
                  <Row label="Loading, unloading, bags" value={inr(route.per_quintal.misc)} />
                  <Row label="Landed cost per quintal" value={inr(route.per_quintal.landed_cost)} strong />
                  <Row label={`Buying locally at ${route.to_state.state}${route.to_state.manual ? " (your rate)" : ""}`} value={inr(route.per_quintal.destination_rate)} />
                  <Row label={route.per_quintal.saving >= 0 ? "You save per quintal" : "You lose per quintal"} value={`${inr(Math.abs(route.per_quintal.saving))} (${route.per_quintal.saving_pct}%)`} strong />
                </div>
              </div>
              <div className="lg:col-span-2 space-y-3">
                <div className={`rounded-lg border p-4 ${v.cls}`}>
                  <div className="text-xs uppercase tracking-widest font-bold opacity-70">For {route.total.quantity_qtl} quintal</div>
                  <div className="font-display text-3xl font-black mt-1">{route.total.saving >= 0 ? "" : "−"}{inr(Math.abs(route.total.saving))}</div>
                  <div className="text-sm mt-1">{v.note}</div>
                </div>
                <p className="text-xs text-stone-500">{route.assumptions.note}</p>
                <button onClick={post} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-semibold" data-testid="trade-post-btn">
                  Post a buy requirement <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {route && (
            <div className="mt-6">
              <div className="font-display font-bold text-stone-900">Open sell lots in {route.from_state.state}</div>
              {route.listings.length === 0 ? (
                <p className="text-sm text-stone-600 mt-1">No open sell lot of {route.commodity} in {route.from_state.state} on India Mandi yet. Post your requirement and sellers there will be matched to you.</p>
              ) : (
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-sm min-w-[480px]">
                    <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-500"><th className="py-2">Where</th><th>Grade</th><th className="text-right">Quantity</th><th className="text-right">Asking rate</th><th /></tr></thead>
                    <tbody>
                      {route.listings.map((l) => (
                        <tr key={l.id} className="border-t border-stone-100">
                          <td className="py-2">{l.district}, {l.state}</td>
                          <td>{l.grade || "–"}</td>
                          <td className="text-right font-mono">{l.quantity} {l.unit}</td>
                          <td className="text-right font-mono">{inr(l.price_expected)}</td>
                          <td className="text-right"><button onClick={post} className="px-3 py-1 rounded border border-emerald-900 text-emerald-900 text-xs font-bold hover:bg-emerald-50">Request</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Section>

        <Section eyebrow="Step 3" title="How the deal runs on India Mandi">
          <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {STEPS.map((s, i) => (
              <li key={s.title} className="rounded-lg border border-stone-200 p-4">
                <div className="flex items-center gap-2 text-emerald-900"><s.icon size={22} weight="duotone" /><span className="text-xs font-bold uppercase tracking-widest text-stone-500">Step {i + 1}</span></div>
                <div className="font-display font-bold mt-2">{s.title}</div>
                <p className="text-sm text-stone-600 mt-1">{s.text}</p>
              </li>
            ))}
          </ol>
        </Section>

        {legal && (
          <Section eyebrow="Before you trade" title="What must be in order, and what to avoid">
            <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 mb-4">
              <Warning size={20} weight="fill" className="shrink-0 mt-0.5" /> <span>{legal.disclaimer}</span>
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 font-display font-bold text-emerald-900 mb-2"><CheckCircle size={20} weight="fill" /> Keep in order</div>
                <div className="space-y-2">
                  {legal.must_do.map((x) => (
                    <details key={x.title} className="rounded-lg border border-stone-200 p-3 group">
                      <summary className="cursor-pointer font-semibold text-sm">{x.title}</summary>
                      <p className="text-sm text-stone-700 mt-2">{x.text}</p>
                      <p className="text-xs text-stone-500 mt-1">Confirm with: {x.confirm_with}</p>
                    </details>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 font-display font-bold text-red-800 mb-2"><Warning size={20} weight="fill" /> Do not do</div>
                <div className="space-y-2">
                  {legal.dont_do.map((x) => (
                    <details key={x.title} className="rounded-lg border border-red-200 bg-red-50/40 p-3">
                      <summary className="cursor-pointer font-semibold text-sm">{x.title}</summary>
                      <p className="text-sm text-stone-700 mt-2">{x.text}</p>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </Section>
        )}

        <p className="text-xs text-stone-500 pb-6">
          Rates are the day's average of the markets that reported to AGMARKNET (data.gov.in), in rupees per quintal{asOf ? `, as of ${asOf}` : ""}. Costs shown are estimates, not a quote.
        </p>
      </main>

      {/* The answer stays in view while the user picks states in the table above */}
      {rates && from && to && (
        <div className="fixed bottom-0 inset-x-0 z-30 border-t border-stone-300 bg-white/95 backdrop-blur shadow-[0_-4px_16px_rgba(0,0,0,0.08)]" data-testid="trade-bar">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm min-w-0">
              <span className="font-bold text-emerald-900">{from}</span>
              <ArrowRight size={14} className="inline mx-1.5 text-stone-500" />
              <span className="font-bold text-orange-700">{to}</span>
              <span className="text-stone-500"> · {commodity} · {qty} qtl</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {from === to ? (
                <span className="text-sm text-amber-800">Pick a different state for "Deliver to" or "Buy from" to compare.</span>
              ) : route && v ? (
                <span className={`px-3 py-1 rounded-full border text-xs font-bold ${v.cls}`}>
                  {v.text} · {route.per_quintal.saving >= 0 ? "save" : "lose"} {inr(Math.abs(route.per_quintal.saving))}/qtl
                </span>
              ) : (
                <span className="text-sm text-stone-500">{routeError || "Working it out…"}</span>
              )}
              <button onClick={() => document.getElementById("trade-route")?.scrollIntoView({ behavior: "smooth" })} className="px-4 py-2 rounded-md bg-emerald-900 hover:bg-emerald-800 text-white text-sm font-bold" data-testid="trade-bar-details">
                See full cost ↓
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
